import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table/kit';
import { CharacterCount } from '@tiptap/extensions';
import { BoardImage, ParagraphFormat } from './editorFormatting.ts';
import { BOARD_HEADING_LEVELS } from './editorOptions.ts';

/**
 * 에디터/뷰어 공통 extension 구성.
 *
 * Tiptap 3.x 기준:
 *  - StarterKit 에 Link/Underline 등 핵심 마크가 포함됨(별도 설치 불필요).
 *  - Image 는 본문 인라인 이미지용. src 는 CDN URL 만 들어간다(base64 금지).
 *  - 뷰어는 editable=false 로 동일 extension 을 재사용해
 *    저장된 JSON 을 작성 시와 똑같이 렌더한다(렌더 불일치 방지).
 *  - 편집 전용 동작(이미지·표 크기 조절, placeholder, 글자 수)은 editable 일 때만 켠다.
 */
export function createBoardExtensions(opts?: {
  placeholder?: string;
  editable?: boolean;
}): Extensions {
  const editable = opts?.editable ?? false;
  return [
    StarterKit.configure({
      heading: { levels: [...BOARD_HEADING_LEVELS] },
      // 링크는 StarterKit 내장. 새 탭 + 보안 rel 부여.
      link: {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      },
    }),
    // 배경색은 형광펜(Highlight)으로, 줄 간격은 문단 속성(ParagraphFormat)으로 따로 둔다.
    TextStyleKit.configure({ backgroundColor: false, lineHeight: false }),
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ParagraphFormat,
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit.configure({ table: { resizable: editable } }),
    BoardImage.configure({
      inline: false,
      allowBase64: false, // base64 차단: jsonb 비대화 방지, URL만 허용
      HTMLAttributes: { class: 'rounded-lg max-w-full h-auto' },
      resize: editable
        ? {
            enabled: true,
            directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
            minWidth: 48,
            minHeight: 48,
            alwaysPreserveAspectRatio: true,
          }
        : false,
    }),
    ...(editable
      ? [
          Placeholder.configure({
            placeholder: opts?.placeholder ?? '내용을 입력하세요…',
          }),
          CharacterCount,
        ]
      : []),
  ];
}

/** 본문이 비었는지 검사(공백/빈 단락만 있는 경우 포함) */
export function isEmptyDoc(
  json: { type?: string; content?: unknown[] } | null | undefined,
): boolean {
  if (!json || !json.content || json.content.length === 0) return true;
  // 단락 1개에 내용 없음 → 빈 문서로 간주 (이미지·구분선처럼 원래 자식이 없는 블록은 내용으로 친다)
  if (json.content.length === 1) {
    const only = json.content[0] as { type?: string; content?: unknown[] };
    if (only.type === 'paragraph' && (!only.content || only.content.length === 0)) return true;
  }
  return false;
}
